'use client'

import { useCallback, useMemo } from 'react'

import { GATEWAY_TIME_RANGE_FILTER_TEST_IDS } from '@apps/service-map/service-map-test-ids.const'
import { selectCustomRangeApplied, selectTimeRangeValue } from '@apps/service-map/stores/service-map.selector'
import { useServiceMapStore } from '@apps/service-map/stores/service-map.store'

import { useGatewayTimeRangeFilterUiConfig } from '../../hooks/useGatewayTimeRangeFilterUiConfig'
import { DEFAULT_TIME_RANGE_VALUE, TIME_RANGE_CUSTOM_VALUE } from '../../service-map.const'
import type { ServiceMapTimeRangeFilterValue } from '../../service-map.interface'
import { formatCustomRangeLabel, ServiceMapTimeRangeFilter, toCustomRange } from '../TimeRangeFilter'
import type {
  ServiceMapTimeRangeCustomApplied,
  ServiceMapTimeRangeFilterLabels,
  ServiceMapTimeRangeFilterOption,
} from '../TimeRangeFilter/time-range.types'

import styles from './GatewayTimeRangeFilter.module.scss'

type GatewayTimeRangeFilterProps = {
  labels?: Partial<ServiceMapTimeRangeFilterLabels>
  options?: readonly ServiceMapTimeRangeFilterOption[]
}

export function GatewayTimeRangeFilter({ labels, options }: GatewayTimeRangeFilterProps) {
  const { options: resolvedOptions, labels: resolvedLabels } = useGatewayTimeRangeFilterUiConfig(labels, options)
  const timeRangeValue = useServiceMapStore(selectTimeRangeValue)
  const customRangeApplied = useServiceMapStore(selectCustomRangeApplied)
  const setTimeRangeFilter = useServiceMapStore((state) => state.setTimeRangeFilter)

  const timeRangeCustomRange = useMemo(() => {
    if (!customRangeApplied) {
      return undefined
    }

    return toCustomRange({
      startAt: new Date(customRangeApplied.startAt),
      endAt: new Date(customRangeApplied.endAt),
    })
  }, [customRangeApplied])

  const timeRangeCustomValue =
    timeRangeValue === TIME_RANGE_CUSTOM_VALUE ? formatCustomRangeLabel(timeRangeCustomRange) : undefined

  const handleTimeRangeApply = useCallback(
    (value: ServiceMapTimeRangeFilterValue, applied?: ServiceMapTimeRangeCustomApplied | null) => {
      setTimeRangeFilter(
        value,
        value === TIME_RANGE_CUSTOM_VALUE && applied
          ? { startAt: applied.startAt.getTime(), endAt: applied.endAt.getTime() }
          : null
      )
    },
    [setTimeRangeFilter]
  )

  return (
    <div className={styles.root} data-testid={GATEWAY_TIME_RANGE_FILTER_TEST_IDS.root}>
      <ServiceMapTimeRangeFilter
        value={timeRangeValue}
        defaultValue={DEFAULT_TIME_RANGE_VALUE}
        customValue={timeRangeCustomValue}
        customRange={timeRangeCustomRange}
        options={[...resolvedOptions]}
        labels={resolvedLabels}
        onApply={handleTimeRangeApply}
      />
    </div>
  )
}
